import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTable, usePagination } from 'react-table';
import { Paper } from '@mui/material';
import ApiBaseURL from '../ApiBaseUrl/ApiBaseURL';
import ReportTabularViewShiftWise from './ReportTabularViewShiftWise';
import ReportSetWiseTrip from './ReportSetWiseTrip';

const ReportTabularView = (props) => {

    const [viewMode, setViewMode] = useState('table');
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();

    const {
        vehicleTypeId,
        vendorId,
        driverId,
        vehicleId,
        period
    } = props.vehicleTypeId ? props : location.state || {};

    const handleViewModeChange = (mode) => {
        setViewMode(mode);
        if (mode === 'graph') {
            navigate('/dshbd');
        } else if (mode === 'table') {
            navigate('/tabular-report');
        }
    };

    const fetchConsumptionData = async () => {
        const userId = localStorage.getItem('userId');
        const token = localStorage.getItem('token');

        if (!userId || !token) {
            console.error('Missing userId or token in localStorage');
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`${ApiBaseURL}/dashboard/v1/get/consumptions`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                    userId: userId,
                },
                body: JSON.stringify({
                    vehicleTypeId,
                    vendorId,
                    driverId,
                    vehicleId,
                    period,
                }),
            });

            const result = await response.json();

            if (response.ok && result.statusDescription.statusCode === 200) {
                const sortedData = (result.data || []).sort((a, b) => new Date(b.tripDate) - new Date(a.tripDate));
                setTableData(sortedData);
                // setTableData(result.data || []);
            } else {
                console.error('API error:', result.statusDescription?.description);
                setTableData([]);
            }
        } catch (error) {
            console.error('Fetch error:', error.message);
            setTableData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchConsumptionData();
    }, [vehicleTypeId, vendorId, driverId, vehicleId, period]);

    const columns = useMemo(
        () => [
            { Header: 'Trip Date', accessor: 'tripDate' },
            // { Header: 'Working Hours', accessor: 'totalWorkingHours' },
            {
                Header: 'Total Trips',
                accessor: 'totalTrips',
                Cell: ({ value }) => new Intl.NumberFormat('en-IN').format(Math.round(value))
            },
            {
                Header: 'Work Done m³',
                accessor: 'totalCubicMeter',
                Cell: ({ value }) => new Intl.NumberFormat('en-IN').format(Math.round(value))
            },
            {
                Header: 'Fuel Consumption (Ltr.)',
                accessor: 'totalFuelQuantity',
                Cell: ({ value }) => new Intl.NumberFormat('en-IN').format(Math.round(value))
            },
            {
                Header: 'Fuel Cost (INR)',
                accessor: 'totalFuelAmount',
                Cell: ({ value }) =>
                    ` ${new Intl.NumberFormat('en-IN').format(Math.round(value))}`
            }
        ],
        []
    );

    const data = useMemo(() => tableData, [tableData]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        nextPage,
        previousPage,
        pageOptions,
        state: { pageIndex },
    } = useTable(
        {
            columns,
            data,
            initialState: { pageIndex: 0, pageSize: 10 },
        },
        usePagination
    );

    return (
        <>
            <div className="staff-management p-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="fw-bold">Daily Summary Reports</h4>
                </div>

                <div className="row align-items-center mb-3">
                    <div className="col d-flex justify-content-end align-items-center">
                        <div className="form-check form-check-inline mb-0">
                            <input
                                className="form-check-input"
                                type="radio"
                                name="viewMode"
                                id="graphicalView"
                                value="graph"
                                checked={viewMode === 'graph'}
                                onChange={() => handleViewModeChange('graph')}
                            />
                            <label className="form-check-label" htmlFor="graphicalView" style={{ cursor: 'pointer' }}>
                                Graphical View
                            </label>
                        </div>

                        <div className="form-check form-check-inline mb-0 ms-3">
                            <input
                                className="form-check-input"
                                type="radio"
                                name="viewMode"
                                id="tabularView"
                                value="table"
                                checked={viewMode === 'table'}
                                onChange={() => handleViewModeChange('table')}
                            />
                            <label className="form-check-label" htmlFor="tabularView" style={{ cursor: 'pointer' }}>
                                Tabular View
                            </label>
                        </div>
                    </div>
                </div>

                <ReportSetWiseTrip />

                <Paper style={{ width: '100%' }} className="mt-2">
                    {loading ? (
                        <div className="text-center py-5">Loading...</div>
                    ) : (
                        <table {...getTableProps()} className="table table-striped table-bordered mb-0">
                            <thead className="thead-dark">
                                {headerGroups.map((headerGroup) => (
                                    <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                                        {headerGroup.headers.map((column) => (
                                            <th {...column.getHeaderProps()} key={column.id}>
                                                {column.render('Header')}
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>

                            <tbody {...getTableBodyProps()}>
                                {page.length === 0 ? (
                                    <tr>
                                        <td colSpan={columns.length} className="text-center py-4">
                                            No data available
                                        </td>
                                    </tr>
                                ) : (
                                    page.map((row) => {
                                        prepareRow(row);
                                        return (
                                            <tr {...row.getRowProps()} key={row.id}>
                                                {row.cells.map((cell) => (
                                                    <td {...cell.getCellProps()} key={cell.column.id}>
                                                        {cell.render('Cell')}
                                                    </td>
                                                ))}
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    )}

                    {!loading && (
                        <div className="pagination d-flex justify-content-between align-items-center px-3 py-2">
                            <button
                                onClick={() => previousPage()}
                                disabled={!canPreviousPage}
                                className="btn btn-sm btn-outline-secondary"
                            >
                                Previous
                            </button>
                            <span>
                                Page <strong>{pageIndex + 1} of {pageOptions.length}</strong>
                            </span>
                            <button
                                onClick={() => nextPage()}
                                disabled={!canNextPage}
                                className="btn btn-sm btn-outline-secondary"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </Paper>
            </div>
            <ReportTabularViewShiftWise />
        </>
    );
};

export default ReportTabularView;